/**
 * Who is signed in, and what the API says they may do — as React context.
 *
 * Two facts, from two places, deliberately kept apart:
 *
 *   session  from Supabase in the browser. Exists or not; drives "show the
 *            sign-in page or the app".
 *   me       from `GET /api/me`. The roles the API will actually judge this
 *            person by. Drives which menu items and routes appear.
 *
 * The browser could read roles out of the token itself, and it must not: the
 * API is the one boundary (D-026), and the menu should reflect what the API
 * will say, not what the browser decoded. One extra request per sign-in buys
 * a single source of truth.
 *
 * A 401 from `/api/me` means the session the browser holds is one the API
 * refuses. Signing out is the only honest response.
 */

import type { MeResponse } from '@unitedos/shared';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiError, fetchMe } from './api.js';
import { signOut, useSession, type SessionState } from './supabase.js';

export type MeState =
  | { readonly state: 'idle' }
  | { readonly state: 'loading' }
  | { readonly state: 'ok'; readonly me: MeResponse }
  | { readonly state: 'error'; readonly message: string };

export interface SessionContextValue extends SessionState {
  readonly me: MeState;
  /** Re-ask the API who we are — after a role change, say. */
  readonly reloadMe: () => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [me, setMe] = useState<MeState>({ state: 'idle' });
  const [generation, setGeneration] = useState(0);

  const userId = session.session?.user.id;

  useEffect(() => {
    if (!userId) {
      setMe({ state: 'idle' });
      return;
    }
    const abort = new AbortController();
    setMe({ state: 'loading' });
    fetchMe(abort.signal)
      .then((data) => setMe({ state: 'ok', me: data }))
      .catch((err: unknown) => {
        if (abort.signal.aborted) return;
        if (err instanceof ApiError && err.unauthorized) {
          void signOut();
          return;
        }
        setMe({ state: 'error', message: err instanceof ApiError ? err.message : 'Could not load your account.' });
      });
    return () => abort.abort();
  }, [userId, generation]);

  const value: SessionContextValue = {
    ...session,
    me,
    reloadMe: () => setGeneration((g) => g + 1),
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSessionContext(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSessionContext must be used inside <SessionProvider>');
  return value;
}

/** The signed-in person's roles, or an empty set while unknown. */
export function useRoles(): MeResponse['roles'] {
  const { me } = useSessionContext();
  return me.state === 'ok' ? me.me.roles : [];
}
